import React, { useRef, useState, useEffect, ReactNode } from 'react';

interface ResponsiveChartProps {
  children: (dimensions: { width: number; height: number }) => ReactNode;
  minHeight?: number;
  debounceMs?: number;
  className?: string;
}

/**
 * ResponsiveChart - Wrapper reusable para gráficos responsivos
 * 
 * Usa ResizeObserver para detectar cambios de tamaño del contenedor
 * y pasa las dimensiones dinámicas a los componentes hijos.
 * 
 * Funciona con:
 * - Resize del grid/layout
 * - Drag & drop
 * - Cambios de viewport (mobile/tablet/desktop)
 * - Apertura/cierre de sidebars
 * 
 * @example
 * <ResponsiveChart>
 *   {({ width, height }) => (
 *     <BarChart width={width} height={height} data={data}>
 *       ...
 *     </BarChart>
 *   )}
 * </ResponsiveChart>
 */
const ResponsiveChart: React.FC<ResponsiveChartProps> = ({ 
  children, 
  minHeight = 200,
  debounceMs = 10, // Reducido para respuesta más rápida
  className = ''
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const resizeTimeoutRef = useRef<NodeJS.Timeout>();
  const frameRef = useRef<number>();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateDimensions = () => {
      if (!container) return;
      
      // Cancelar cualquier animación frame pendiente
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
      
      // Usar requestAnimationFrame para sincronizar con el repaint del browser
      frameRef.current = requestAnimationFrame(() => {
        const width = container.clientWidth;
        const height = container.clientHeight;
        
        setDimensions(prev => {
          const newWidth = Math.floor(width);
          const newHeight = Math.max(Math.floor(height), minHeight);
          
          // Solo actualizar si hay cambio real (>2px para ser sensible)
          const widthDiff = Math.abs(prev.width - newWidth);
          const heightDiff = Math.abs(prev.height - newHeight);
          
          if (widthDiff > 2 || heightDiff > 2) {
            console.log('📊 Chart resize:', { width: newWidth, height: newHeight });
            return { width: newWidth, height: newHeight };
          }
          return prev;
        });
      });
    };

    // ResizeObserver para detectar cambios de tamaño inmediatamente
    const resizeObserver = new ResizeObserver((entries) => {
      // Limpiar timeout anterior
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      // Durante resize activo, actualizar inmediatamente
      updateDimensions();
      
      // Timeout corto para capturar el valor final
      resizeTimeoutRef.current = setTimeout(() => {
        updateDimensions();
      }, debounceMs);
    });

    resizeObserver.observe(container);

    // Medición inicial inmediata
    updateDimensions();

    return () => {
      resizeObserver.disconnect();
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [minHeight, debounceMs]);

  return (
    <div 
      ref={containerRef} 
      className={`w-full h-full ${className}`}
      style={{ minHeight: `${minHeight}px` }}
    >
      {dimensions.width > 0 && dimensions.height > 0 && children(dimensions)}
    </div>
  );
};

export default ResponsiveChart;
